import { useCallback, useEffect, useMemo } from 'react'
import {
    CustomerLookupContract,
    LoyaltyRedeemPointRuleSettingContract,
    OrderPaymentStatus,
    PaymentContract,
    PaymentSettingContract,
    ReceiptTemplateMarkupSlimModel,
    TipsSettingContract,
} from 'models'
import { PaymentProvider } from './hook/usePaymentContext'
import Decimal from 'decimal.js'
import { usePaymentDialog } from './hook/usePaymentDialog'
import PaymentProcessDialog from './dialogs/PaymentProcessDialog'
import { ProcessingSaleCallback } from './Models/ProcessingSaleCallback'
import { PaymentResult } from './Models/PaymentResult'
import { SaleResultModel } from './Models/SaleResultModel'
import { OrderDetailModel } from './Models/OrderDetailModels'
import { ReceiptSaleModel, ReceiptDialog } from '../ReceiptDialog/ReceiptDialog'
import PaymentErrorDialog from './dialogs/PaymentErrorDialog'
import { PaymentDisplaySection } from './hook/SectionType'
import { useSaleDependenciesContext } from 'views/Sale/contexts/SaleDependenciesContext'

export interface IPaymentDialogProps {
    open: boolean
    /**
     * order processing or in state that can't process payment
     */
    isBusy?: boolean
    /** order total due */
    totalDueAmount: number
    initPayments: PaymentContract[]
    /**
     * order customer
     */
    orderCustomerID?: string
    orderDetail?: OrderDetailModel
    sellReceiptTemplate?: ReceiptTemplateMarkupSlimModel
    hasAwaitingProcessTransaction?: boolean
    /**
     * manual call API to do the finalize order.
     */
    finalizeOrder?: () => Promise<void>
    onCloseReceiptDialog?: (isLogout: boolean) => void

    customerList: CustomerLookupContract[]
    /**
     * do when sale is paid
     * @param saleResult
     * @returns
     */
    onFinishSalePayment?: (
        saleResult: PaymentResult,
        isOffline?: boolean,
    ) => void
    /**
     * do when the payment is paid, if split payment will do every the payment is received
     */
    onFinishSinglePayment?: (
        saleResult: PaymentResult,
        isOffline?: boolean,
    ) => void
    /**
     * do when submit the payment request error
     * @param error
     * @returns
     */
    onPaymentError?: (error: any) => void
    onCloseDialog: () => void
    onSelectCustomer?: () => void

    paymentSettings: PaymentSettingContract[]
    tipsSetting: TipsSettingContract
    pointRedemptionSetting?: LoyaltyRedeemPointRuleSettingContract | undefined

    useSplitPayment?: boolean
    useTip?: boolean
    useRedeempoint?: boolean

    onProcessingSale: ProcessingSaleCallback
    getSaleDetailByID: (id: string) => Promise<SaleResultModel | undefined>
    isOffline?: boolean
    useLogoutButton?: boolean
}

export function PaymentDialog({
    open,
    isBusy = false,
    initPayments = [],
    totalDueAmount,
    customerList,
    paymentSettings,
    tipsSetting,
    orderCustomerID,
    sellReceiptTemplate,
    pointRedemptionSetting,
    orderDetail,
    useSplitPayment = true,
    useTip = true,
    useRedeempoint = true,
    hasAwaitingProcessTransaction,
    onPaymentError,
    onSelectCustomer,
    getSaleDetailByID,
    onProcessingSale,
    finalizeOrder,
    onFinishSinglePayment,
    onFinishSalePayment,
    onCloseReceiptDialog,
    onCloseDialog,
    isOffline,
    useLogoutButton = false,
}: IPaymentDialogProps) {
    const isNegativeTotalDue = useMemo(
        () => (orderDetail?.totalDueAmount ?? 0) < 0,
        [orderDetail],
    )

    const {
        sale,
        openReceipt,
        isSubmiting,
        paymentDetail,
        quickButtons,
        customerAccounts,
        onCloseReceipt,
        onSelectPaymentType,
        onSetExpectAmount,
        onSelectCustomerAccount,
        onSubmitPayment,
        onCloseAccountDialog,
        fetchAccounts,
        isLoadAccount,
        setPaidPayments,
        paidPayments,
        selectedCustomerAccount,
        selectedPaymentType,
        payment,
        expectAmount,
        onSetTenderedAmount,
        changeValue,
        isSaleComplete,
        isSplitPayment,
        onSetSplitPayment,
        onCloseDialogWithReset,
        paymentAccountCustomerID,
        onSelectedPaymentCustomerID,
        currentSection,
        onSetDisplaySection,
        selectedGiftCard,
        onSelectGiftCard,
        currencyID,
        onEnterAddTipsMode,
        onExitAddTipsMode,
        addTipsMode,
        onSetTipsAmount,
        tipsAmount,
        tipsType,
        clearTips,
        tipsRate,
        defaultPaymentType,
        noQuickTip,
        pointRedeem,
        redeemDiscount,
        onRedeemSubmit,
        setShowRedeemPointForm,
        showRedeemPointForm,
        isAddTipsEnabled,
        onSetReceivedCash,

        paymentProcessStatus,
        evoPaymentTransactionId,
        checkIntegrationPaymentStatus,
        isCheckingEvoPaymentStatus,
        resetEvoPaymentDialogState,
        errorMessage,
        additionalInstruction,

        paymentIntegrationPaymentID,
        clearPaymentState,
        paymentForms,
        handlePaymentFormsChange,
        onQuickSplitGiftCardPayment,
        paymentTypeSelections,
        setSplitPaymentWithOutDeps,
        tenderedAmount,
    } = usePaymentDialog({
        paymentSettings,
        tipsSetting,
        orderCustomerID,
        orderID: orderDetail?.orderID,
        finalizeOrder,
        onCloseReceiptDialog,
        onFinishSalePayment,
        onPaymentError,
        onCloseDialog,
        useTip,
        onProcessingSale,
        getSaleDetailByID,
        onFinishSinglePayment,
        isNegativeTotalDue,
        isOffline,
        totalDueAmount,
    })

    const isReadyToProceedPayment = useMemo(
        () => Boolean(orderDetail?.orderID) && !isSubmiting && !isBusy,
        [orderDetail?.orderID, isSubmiting, isBusy],
    )

    const handleOnSubmitPayment = useCallback(async () => {
        return await onSubmitPayment()
    }, [onSubmitPayment])

    useEffect(() => {
        if (!isSplitPayment && open) {
            onSetExpectAmount(new Decimal(totalDueAmount))
            handlePaymentFormsChange([])
        }
    }, [
        handlePaymentFormsChange,
        isSplitPayment,
        onSetExpectAmount,
        open,
        selectedPaymentType?.paymentTypeID,
        totalDueAmount,
    ])

    useEffect(() => {
        if (open) {
            if (!isOffline) fetchAccounts()
        } else {
            handlePaymentFormsChange([])
        }
    }, [fetchAccounts, open, isOffline, handlePaymentFormsChange])

    useEffect(() => {
        if (
            initPayments.length > 0 &&
            orderDetail?.orderPaymentStatus !== OrderPaymentStatus.Paid
        ) {
            setPaidPayments(initPayments)
            setSplitPaymentWithOutDeps(true)
        }
    }, [
        initPayments,
        onSetSplitPayment,
        setPaidPayments,
        orderDetail?.orderPaymentStatus,
        setSplitPaymentWithOutDeps,
    ])

    //init payment type
    useEffect(() => {
        if (open && defaultPaymentType && !selectedPaymentType)
            onSelectPaymentType(defaultPaymentType.paymentSettingID!)
    }, [defaultPaymentType, onSelectPaymentType, open, selectedPaymentType])

    const { defaultReceiptMarkups } = useSaleDependenciesContext()
    const saleReceipt: ReceiptSaleModel | undefined = useMemo(() => {
        if (sale)
            return {
                saleID: sale?.saleID!,
                //pre fill customer email to receipt
                customerEmail:
                    customerList.find((a) => a.customerID === orderCustomerID)
                        ?.email ?? '',
            }
    }, [customerList, orderCustomerID, sale])
    return (
        <PaymentProvider
            value={{
                onSetReceivedCash,
                isReadyToProceedPayment,
                paymentTypeLists: paymentTypeSelections,
                paidPayments,
                isProcessing: isSubmiting,
                orderTotalDue: new Decimal(totalDueAmount),
                loadingCustomerPaymentAccount: isLoadAccount,
                paymentAccounts: customerAccounts,
                onOpenAccountDialog: () =>
                    onSetDisplaySection(PaymentDisplaySection.AccountList),
                onCloseAccountDialog,
                selectedCustomerAccount,
                quickButtons,
                selectedPaymentType,
                //for cfd that will not display payment when close the payment dialog
                payment: open ? payment : undefined,
                expectAmount,
                tenderedAmount: new Decimal(tenderedAmount),
                paymentDetail,
                onSetExpectAmount,
                onSetTenderedAmount,
                onSelectCustomerAccount,
                onSelectPaymentType,
                onSubmitPayment: handleOnSubmitPayment,

                orderCustomerID,
                customerList,
                paymentAccountCustomerID,
                onSelectedPaymentCustomerID,
                currentSection,
                onSetDisplaySection,
                selectedGiftCard,
                onSelectGiftCard,
                currencyID,
                onChangePaymentForms: handlePaymentFormsChange,
                paymentForms,
                addTipsMode,
                onEnterAddTipsMode,
                onExitAddTipsMode,
                onSetTipsAmount,
                tipsAmount,
                tipsType,
                clearTips,
                tipsRate,
                isSplitPayment,
                onQuickSplitGiftCardPayment,
                defaultPaymentType,
                noQuickTip,
                pointRedemptionSetting,
                showRedeemPointForm,
                onSetShowRedeemPointForm: setShowRedeemPointForm,
                pointRedeem,
                redeemDiscount,
                onRedeemSubmit,
                onClickCustomer: onSelectCustomer,
                orderDetail,
                onCloseDialogWithReset,
                onSetSplitPayment,
                isSaleComplete,
                changeValue,
                isAddTipsEnabled,
                useSplitPayment,

                //Evo Payment
                resetPaymentStatus: clearPaymentState,
                evoPaymentTransactionId,
                checkEvoPaymentStatus: checkIntegrationPaymentStatus,
                isCheckingEvoPaymentStatus,
                resetEvoPaymentDialogState,
                paymentIntegrationProcessStatus: paymentProcessStatus,
                errorMessage,
                additionalInstruction,
                paymentIntegrationPaymentID,
                isNegativeTotalDue,
                hasAwaitingProcessTransaction,
            }}
        >
            <>
                <PaymentProcessDialog
                    open={open && !isSaleComplete}
                    onCloseDialogWithReset={onCloseDialogWithReset}
                />
                <ReceiptDialog
                    paymentTypeID={selectedPaymentType?.paymentTypeID}
                    totalDuleValue={expectAmount.toNumber()}
                    sellReceiptTemplate={
                        sellReceiptTemplate ?? defaultReceiptMarkups?.sale
                    }
                    sale={saleReceipt}
                    changeValue={changeValue.toNumber()}
                    isSaleComplete={isSaleComplete}
                    openReceipt={openReceipt}
                    onClose={onCloseReceipt}
                    showLogoutButton={useLogoutButton}
                />
                <PaymentErrorDialog />
            </>
        </PaymentProvider>
    )
}
